import { PopupButtonType, PopupIconType, showPopup } from "@kalyzee/kast-app-web-components";
import React, { createRef } from "react";
import { getSubtitlesCost } from "../../helpers/subtitles";
import styles from "./subtitles.module.css";

// The confirmation shown before a transcription runs outside its window: what it costs the host.
// The figures are per model (helpers/subtitles), the model name comes from the server's context.

export interface SubtitlesCostPopupOptions {
  model?: string;
  /** One record, or every waiting job of the server (« Tout transcrire maintenant »). */
  scope: "record" | "all";
  /** The transcription already ended for this build: it is redone from scratch. */
  redo?: boolean;
  /** Offer the level-2 checkbox — the busy check off — per record only; the panel never does. */
  allowBusy?: boolean;
  onConfirm: (busy: boolean) => void | Promise<void>;
}

export const showSubtitlesCostPopup = ({ model, scope, redo, allowBusy, onConfirm }: SubtitlesCostPopupOptions) => {
  const { model: modelName, cost, measured } = getSubtitlesCost(model);
  const busyRef = createRef<HTMLInputElement>();
  const intro =
    scope === "all"
      ? "Les transcriptions en attente sur ce serveur démarrent dès maintenant, sans attendre la fenêtre horaire (une classe en cours les retient encore)."
      : redo
      ? "La transcription est refaite depuis le début, dès maintenant, sans attendre la fenêtre horaire ; le transcript déjà livré au stockage sera remplacé."
      : "La transcription démarre dès maintenant, sans attendre la fenêtre horaire.";
  showPopup({
    title: scope === "all" ? "Tout transcrire maintenant" /* TRANSLATION */ : "Transcrire maintenant" /* TRANSLATION */,
    iconTitle: PopupIconType.WARNING,
    content: (
      <div className={styles.costPopup}>
        <div className={styles.costIntro}>{intro /* TRANSLATION */}</div>
        <div className={styles.costTitle}>
          {`Ce que cela coûte à la machine (modèle ${modelName}${measured ? "" : ", chiffres du modèle large-v3"}) :` /* TRANSLATION */}
        </div>
        <ul className={styles.costList}>
          <li>{`GPU : jusqu'à ${cost.vramPeakGb} Go de VRAM au pic, partagée avec l'encodage du moteur vidéo.` /* TRANSLATION */}</li>
          <li>{`RAM : moins de ${cost.ramPerPieceGb} Go par morceau de dix minutes d'audio.` /* TRANSLATION */}</li>
          <li>
            {`Démarrage : ${cost.modelLoadSeconds} s de chargement du modèle, jusqu'à ${cost.firstAnswerMinutes} min avant la première réponse.` /* TRANSLATION */}
          </li>
        </ul>
        <div className={styles.costNote}>
          {"Le conteneur s'arrête de lui-même à la fin du travail, dès qu'un flux démarre, ou sur « Arrêter ». Le forçage expire au bout de 6 heures." /* TRANSLATION */}
        </div>
        {allowBusy ? (
          <label className={styles.costCheckbox}>
            <input type="checkbox" defaultChecked={false} ref={busyRef} />
            <span>{"Même si un flux est en cours — le direct partage alors le GPU avec la transcription." /* TRANSLATION */}</span>
          </label>
        ) : null}
      </div>
    ),
    buttons: [
      { type: PopupButtonType.CANCEL, element: "Annuler" /* TRANSLATION */ },
      {
        type: PopupButtonType.VALIDATE,
        element: scope === "all" ? "Tout lancer" /* TRANSLATION */ : "Lancer" /* TRANSLATION */,
        onClick: async () => {
          await onConfirm(!!busyRef.current?.checked);
          return true;
        },
      },
    ],
    enableBackdropDismiss: true,
    enableCloseButton: true,
  });
};

export default showSubtitlesCostPopup;
